import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
    GetProgramDetailCancelToken,
    GetShortCourseData,
    getPmcWebsitePages,
    getVersionData,
} from "../services/ProgramService";
import { lockAllProgrammeGrades } from "../services/GradeSettingService";

/**
 * Drives the Programs/PMC page shell: programme/short-course detail
 * fetching per tab, version data, website pages, and the top-level
 * lock/unlock-all-grades flow.
 */
function usePrograms() {
    const history = useHistory();
    const { tab, type, id } = useParams();
    const programId = id;
    const [programName, setProgrammeName] = useState("");
    const [detail, setDetail] = useState([]);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [QAview, setQAview] = useState(false);
    const [refresh, setRefresh] = useState(Date.now());
    const cancelTokenSources = useRef([]);

    const [versionData, setVersionData] = useState([]);
    const [vDataloading, setVDataLoading] = useState(false);
    const [versionDataError, setVersionDataError] = useState(false);
    /* Tracks the versionId the last fetch was for, so a post-Save `refresh` bump (same
     * versionId) never clears `versionData` - only an actual navigation to a different
     * version does. See the fetchVersionData effect below. */
    const previousVersionIdRef = useRef(null);

    const [gradeLockLoading, setGradeLockLoading] = useState({});
    const [linkedQualifications, setLinkedQualifications] = useState([]);
    /*
     * Mirrors useLinkedProgrammes' own `loading` flag (set via LinkedProgrammes.js) so
     * this page's Lock/Unlock-All-Grades buttons — which only render once
     * linkedQualifications is non-empty — can show a same-size placeholder while that
     * fetch is in flight, instead of popping in and shifting the toolbar row/table
     * below it every time this tab is (re)loaded.
     */
    const [linkedQualificationsLoading, setLinkedQualificationsLoading] = useState(true);
    const [updateData, setUpdateData] = useState(false);
    const [linkProgrammeOpen, setLinkProgrammeOpen] = useState(false);
    const [topGradeConfirmation, setTopGradeConfirmation] =
        useState(null);
    const [websitePages, setWebsitePages] = useState([]);
    const [websitePagesLoading, setWebsitePagesLoading] = useState(false);

    useEffect(() => {
        /* Website Page is a PMC Programme feature only (Programme_Management_Center,
        * page_type 0) — Programme_Management_Center.Id and Shortcourse_Management_Center.Id
        * are independent auto-increment sequences, so without this tab/type check a
        * short course could coincidentally share an id with an unrelated programme and
        * surface that programme's website page instead.
        */
        const isProgrammeView =
            (tab === "programme" &&
                (type === "open" || type === "duplicate")) ||
            tab === "features-benefits" ||
            tab === "" ||
            tab === "linked_programmes" ||
            tab === "graduates" ||
            tab === "versions" ||
            tab === "softwareused" ||
            (tab === "programmeDates" && type === "table") ||
            tab === "resources" ||
            (tab === "auditTrail" && type === "open");

        const pmcId = isProgrammeView ? detail?.Id : null;

        if (!pmcId) {
            setWebsitePages([]);
            setWebsitePagesLoading(false);
            return undefined;
        }

        let active = true;
        const source = axios.CancelToken.source();

        const fetchWebsitePages = async () => {
            setWebsitePagesLoading(true);
            try {
                const response = await getPmcWebsitePages(pmcId, source.token);
                if (active) {
                    setWebsitePages(response?.data?.pages || []);
                }
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error("usePrograms.js->getPmcWebsitePages->Error", error);
                    if (active) {
                        setWebsitePages([]);
                    }
                }
            } finally {
                if (active) {
                    setWebsitePagesLoading(false);
                }
            }
        };

        fetchWebsitePages();

        return () => {
            active = false;
            source.cancel("New request made");
        };
    }, [tab, type, detail?.Id]);

    const { search } = useLocation();
    const query = new URLSearchParams(search);

    const queryVersionId = query.get("versionId");
    const queryProgrammeId = query.get("programmeId");
    const queryType = query.get("type");

    useEffect(() => {
        if (
            Object.keys(detail).length > 0 &&
            tab &&
            // 1 = study-path-linked re-registration is enabled for this programme
            Number(detail?.EnableStudypathLinkReReg) !== 1
        ) {
            const tempTabNotAllowedList = ["versions"];

            if (tempTabNotAllowedList.includes(tab)) {
                history.push(
                    `/program-management-center/programmes/programme/open/${
            queryProgrammeId ?? id
          }`
                );
            }
        }
    }, [detail, tab]);

    const isShortCourseDetailTab =
        (tab === "shortCourse" &&
            (type === "open" || type === "duplicate")) ||
        ((tab === "shortCourseFeatures-benefits" ||
            tab === "shortCourse-resources" ||
            tab === "shortCoursetopics" ||
            tab === "shortCourseOutcomes" ||
            tab === "courseDates") &&
            type === "table") ||
        (tab === "shortCourseAuditTrail" && type === "open");

    const isProgrammeDetailTab =
        (tab === "programme" &&
            (type === "open" || type === "duplicate")) ||
        tab === "features-benefits" ||
        tab === "" ||
        tab === "linked_programmes" ||
        tab === "graduates" ||
        tab === "versions" ||
        (tab === "programmeDates" && type === "table") ||
        tab === "resources" ||
        (tab === "auditTrail" && type === "open");

    useEffect(() => {
        let active = true;
        const detailCancelTokenSources = [];

        const programmeDetails = async () => {
            detailCancelTokenSources.forEach((source) => {
                source.cancel("New request made");
            });

            const source = axios.CancelToken.source();
            detailCancelTokenSources.push(source);

            try {
                const res = await GetProgramDetailCancelToken(
                    {
                        PMC_ID: queryProgrammeId ?? id,
                    },
                    source.token
                );

                if (!active) {
                    return;
                }

                if (res.status === 200) {
                    setProgrammeName(res.data[0].name);
                    setDetail(res.data[0]);

                    if (!res.data[0]?.Id) {
                        Swal.fire({
                            icon: "Info",
                            title: "Info",
                            text: "No record found with this id",
                        });

                        history.push(
                            "/program-management-center/programmes/programme/table"
                        );
                    }
                }
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error("usePrograms.js->programmeDetails->Error", error);
                }
            } finally {
                if (active) {
                    setIsDetailLoading(false);
                }
            }
        };

        const shortCourseDetail = async () => {
            detailCancelTokenSources.forEach((source) => {
                source.cancel("New request made");
            });

            const source = axios.CancelToken.source();
            detailCancelTokenSources.push(source);

            try {
                const res = await GetShortCourseData(
                    {
                        id: queryProgrammeId ?? id,
                    },
                    source.token
                );

                if (!active) {
                    return;
                }

                if (res.status === 200) {
                    const respData = res.data?.result;

                    setProgrammeName(respData?.name);
                    setDetail(respData);
                }
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error("usePrograms.js->shortCourseDetail->Error", error);
                }

                if (error?.response?.data?.message === "No data found") {
                    Swal.fire({
                        icon: "Info",
                        title: "Info",
                        text: "No record found with this id",
                    });

                    history.push(
                        "/program-management-center/programmes/shortCourse/table"
                    );
                }
            } finally {
                if (active) {
                    setIsDetailLoading(false);
                }
            }
        };

        if (isShortCourseDetailTab) {
            setIsDetailLoading(true);
            shortCourseDetail();
        } else if (isProgrammeDetailTab) {
            setIsDetailLoading(true);
            programmeDetails();
        } else {
            setIsDetailLoading(false);
        }

        return () => {
            active = false;
            detailCancelTokenSources.forEach((source) => {
                source.cancel("Component unmounted");
            });
            setDetail([]);
        };
    }, [id, refresh, queryProgrammeId, isShortCourseDetailTab, isProgrammeDetailTab, history]);

    useEffect(() => {
        cancelTokenSources.current.forEach((source) => {
            source.cancel("New version request made");
        });

        cancelTokenSources.current = [];

        let active = true;

        const fetchVersionData = async () => {
            const source = axios.CancelToken.source();
            cancelTokenSources.current.push(source);

            setVDataLoading(true);
            setVersionDataError(false);

            try {
                const res = await getVersionData({ id: queryVersionId }, source.token);

                if (!active) {
                    return;
                }

                if (!res.data?.getVersion?.id) {
                    Swal.fire({
                        icon: "info",
                        title: "Info",
                        text: "No record found with this id",
                    });

                    history.push(
                        `/program-management-center/programmes/versions/table/${queryProgrammeId}`
                    );
                } else {
                    setVersionData(res.data?.getVersion);
                    setProgrammeName(
                        res.data?.getVersion?.name || ""
                    );
                }
            } catch (err) {
                if (!axios.isCancel(err)) {
                    console.error("usePrograms.js->fetchVersionData->Error", err);
                    if (active) {
                        setVersionDataError(true);
                    }
                }
            } finally {
                if (active) {
                    setVDataLoading(false);
                }
            }
        };

        if (queryVersionId && tab === "versions") {
            /*
             * Only an actual navigation to a different version clears the stale data - a
             * post-Save `refresh` bump (queryVersionId unchanged) must not, or every save
             * would flash the loading skeleton for no reason.
             */
            if (previousVersionIdRef.current !== null && previousVersionIdRef.current !== queryVersionId) {
                setVersionData([]);
            }
            previousVersionIdRef.current = queryVersionId;
            fetchVersionData();
        }

        return () => {
            active = false;
        };
    }, [queryVersionId, queryProgrammeId, refresh, tab]);

    const runTopGradeAction = async (status) => {
        // 1 = lock grades, 0 = unlock grades
        const loadingKey =
            status === 1 ? "lockAll" : "unlockAll";

        setTopGradeConfirmation(null);

        if (!linkedQualifications?.length) {
            return;
        }

        setGradeLockLoading((prev) => ({
            ...prev,
            [loadingKey]: true,
        }));

        try {
            const res = await lockAllProgrammeGrades(
                linkedQualifications,
                status,
                id
            );

            Swal.fire({
                icon: "success",
                title: "Success",
                text:
                    res?.data?.message ||
                    `Grades ${
            status === 1 ? "locked" : "unlocked" // 1 = locked, 0 = unlocked
          } successfully`,
            });

            setUpdateData((prev) => !prev);
        } catch (err) {
            console.error("usePrograms.js->runTopGradeAction->Error", err);

            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    err?.response?.data?.message ??
                    err?.response?.data?.error ??
                    "Unable to update grades",
            });
        } finally {
            setGradeLockLoading((prev) => ({
                ...prev,
                [loadingKey]: false,
            }));
        }
    };

    const handleLockAllGrades = () => {
        if (!linkedQualifications?.length) {
            return;
        }

        setTopGradeConfirmation({
            type: "lock",
            count: linkedQualifications.length,
        });
    };

    const handleUnlockAllGrades = () => {
        if (!linkedQualifications?.length) {
            return;
        }

        setTopGradeConfirmation({
            type: "unlock",
            count: linkedQualifications.length,
        });
    };
    return {
        history,
        tab,
        type,
        id,
        programId,
        programName,
        detail,
        isDetailLoading,
        QAview,
        setQAview,
        refresh,
        setRefresh,
        versionData,
        vDataloading,
        setVDataLoading,
        versionDataError,
        gradeLockLoading,
        setGradeLockLoading,
        linkedQualifications,
        setLinkedQualifications,
        linkedQualificationsLoading,
        setLinkedQualificationsLoading,
        updateData,
        setUpdateData,
        linkProgrammeOpen,
        setLinkProgrammeOpen,
        topGradeConfirmation,
        setTopGradeConfirmation,
        websitePages,
        websitePagesLoading,
        queryVersionId,
        queryProgrammeId,
        queryType,
        runTopGradeAction,
        handleLockAllGrades,
        handleUnlockAllGrades,
    };
}

export default usePrograms;
